export type Locale = 'es' | 'en';

interface CompressorText {
  uploader: {
    limitReachedTitle: string;
    limitReachedDesc: string;
    fileLimitTitle: string;
    fileLimitDesc: (n: number) => string;
    invalidFilesTitle: string;
    invalidFilesDesc: (n: number, formats: string) => string;
    noPendingTitle: string;
    noPendingDesc: string;
    zipFilePrefix: string;
    downloadCompleteTitle: string;
    downloadCompleteDesc: (n: number, zipName: string) => string;
    noCompressedTitle: string;
    noCompressedDesc: string;
    downloadErrorTitle: string;
    downloadErrorDesc: string;
    dropzoneLimitTitle: string;
    dropzoneLimitDesc: string;
    dropHere: string;
    willAppearHere: string;
    ctaButton: string;
    reduceTime: string;
    upToSize: (size: string) => string;
    processingAuto: string;
    compressingImages: string;
    optimizedReady: (n: number) => string;
    doneSingle: string;
    doneMulti: (n: number) => string;
    readyToUse: string;
    sizeSummary: (a: string, b: string, pct: number) => string;
    alreadyOptimized: string;
    creatingZip: string;
    downloadAllZip: string;
    clear: string;
    tryAnother: string;
  };
  imagePreview: {
    status: string;
    error: string;
  };
  actionButtons: {
    downloadOptimized: string;
    readyToUse: string;
    retry: string;
    maxRetriesReached: string;
  };
  fileInfo: {
    original: string;
    optimized: string;
    saved: string;
    alreadyOptimized: string;
  };
  statusBadge: {
    queued: string;
    processing: string;
    completed: string;
    error: (retryCount: number, maxRetryAttempts: number) => string;
    unknown: string;
  };
  imageComparison: {
    original: string;
    compressed: string;
    originalAlt: (fileName: string) => string;
    compressedAlt: (fileName: string) => string;
  };
  toasts: {
    dailyLimitTitle: string;
    dailyLimitDesc: (freeLimit: number) => string;
    dailyLimitDescShort: string;
    compressedTitle: string;
    compressedDesc: (fileName: string) => string;
    unknownCompressionError: string;
    compressionErrorTitle: string;
    compressionErrorDesc: (fileName: string, errorMessage: string) => string;
    compressionLimitTitle: string;
    compressionLimitDesc: (remaining: number) => string;
    unlimited: string;
  };
  exampleImages: {
    heading: string;
    landscape: string;
    portrait: string;
    product: string;
    exampleAlt: (label: string) => string;
    loading: string;
  };
  compressionSlider: {
    originalAlt: string;
    uncompressed: (size: string) => string;
    compressedAlt: string;
    compressed: (size: string) => string;
    quality: string;
    savings: (percent: number, size: string) => string;
    qualityAriaLabel: string;
    maxCompression: string;
    maxQuality: string;
    downloadExample: string;
    compressMyPhotos: string;
  };
}

export const compressorText: Record<Locale, CompressorText> = {
  es: {
    uploader: {
      limitReachedTitle: 'Límite alcanzado',
      limitReachedDesc: 'Ya tienes muchas imágenes. Elimina algunas para agregar más.',
      fileLimitTitle: 'Límite de archivos',
      fileLimitDesc: (n) => `${n} archivo(s) no se pudieron agregar.`,
      invalidFilesTitle: 'Archivos no válidos',
      invalidFilesDesc: (n, formats) => `${n} archivo(s) no son ${formats} válidos o exceden 50MB.`,
      noPendingTitle: 'Sin archivos pendientes',
      noPendingDesc: 'No hay imágenes pendientes para comprimir.',
      zipFilePrefix: 'imagenes_comprimidas',
      downloadCompleteTitle: 'Descarga completada',
      downloadCompleteDesc: (n, zipName) => `${n} imagen(es) descargada(s) en ${zipName}.`,
      noCompressedTitle: 'Sin archivos comprimidos',
      noCompressedDesc: 'No hay imágenes comprimidas para descargar.',
      downloadErrorTitle: 'Error en la descarga',
      downloadErrorDesc: 'No se pudo crear el archivo ZIP. Intenta nuevamente.',
      dropzoneLimitTitle: 'Límite de imágenes alcanzado',
      dropzoneLimitDesc: 'Elimina algunas imágenes para agregar más',
      dropHere: 'Suelta las imágenes aquí',
      willAppearHere: 'Tu imagen aparecerá aquí y se optimizará automáticamente',
      ctaButton: 'Sube tu imagen — optimización automática en segundos',
      reduceTime: 'Reduce el tamaño en menos de 10 segundos',
      upToSize: (size) => `Hasta ${size} por imagen`,
      processingAuto: '⏳ Procesando imagen automáticamente...',
      compressingImages: 'Comprimiendo imágenes...',
      optimizedReady: (n) => n === 1 ? 'Imagen optimizada lista' : `${n} imágenes optimizadas listas`,
      doneSingle: '¡Listo! Tu imagen ya está optimizada',
      doneMulti: (n) => `¡Listo! Tus ${n} imágenes ya están optimizadas`,
      readyToUse: 'Lista para usar en formularios, CV, DNI o email',
      sizeSummary: (a, b, pct) => `De ${a} a ${b} · ${pct}% menos`,
      alreadyOptimized: 'La imagen ya estaba optimizada — se mantuvo la mejor calidad posible',
      creatingZip: 'Creando ZIP...',
      downloadAllZip: 'Descargar todas (ZIP)',
      clear: 'Limpiar',
      tryAnother: 'Probar otra imagen',
    },
    imagePreview: {
      status: 'Estado:',
      error: 'Error:',
    },
    actionButtons: {
      downloadOptimized: 'Descargar imagen optimizada',
      readyToUse: 'Lista para usar en formularios, CV, DNI o email',
      retry: 'Reintentar',
      maxRetriesReached: 'Máximo de intentos alcanzado',
    },
    fileInfo: {
      original: 'Original: ',
      optimized: 'Optimizada: ',
      saved: 'Ahorraste: ',
      alreadyOptimized: 'Imagen ya optimizada — calidad conservada',
    },
    statusBadge: {
      queued: 'En cola',
      processing: 'Procesando...',
      completed: 'Completado',
      error: (retryCount, maxRetryAttempts) => `Error (${retryCount}/${maxRetryAttempts})`,
      unknown: 'Desconocido',
    },
    imageComparison: {
      original: 'Original',
      compressed: 'Comprimida',
      originalAlt: (fileName) => `Original ${fileName}`,
      compressedAlt: (fileName) => `Comprimida ${fileName}`,
    },
    toasts: {
      dailyLimitTitle: 'Límite diario alcanzado',
      dailyLimitDesc: (freeLimit) => `Has alcanzado tu límite de ${freeLimit} compresiones gratuitas por día. Actualiza a Premium para comprimir imágenes ilimitadas.`,
      dailyLimitDescShort: 'Has alcanzado tu límite de compresiones gratuitas por día.',
      compressedTitle: 'Imagen comprimida',
      compressedDesc: (fileName) => `${fileName} comprimida exitosamente`,
      unknownCompressionError: 'Error desconocido al comprimir la imagen',
      compressionErrorTitle: 'Error de compresión',
      compressionErrorDesc: (fileName, errorMessage) => `Error en ${fileName}: ${errorMessage}`,
      compressionLimitTitle: 'Límite de compresión',
      compressionLimitDesc: (remaining) => `Solo puedes comprimir ${remaining} imagen(es) más hoy. Actualiza a Premium para comprimir imágenes ilimitadas.`,
      unlimited: 'Ilimitado',
    },
    exampleImages: {
      heading: 'Prueba con una imagen de ejemplo',
      landscape: 'Paisaje',
      portrait: 'Retrato',
      product: 'Producto',
      exampleAlt: (label) => `Ejemplo ${label}`,
      loading: 'Cargando...',
    },
    compressionSlider: {
      originalAlt: 'Imagen original',
      uncompressed: (size) => `Sin comprimir · ${size}`,
      compressedAlt: 'Imagen comprimida',
      compressed: (size) => `Comprimida · ${size}`,
      quality: 'Calidad: ',
      savings: (percent, size) => `Ahorro: ${percent}% · ${size} menos`,
      qualityAriaLabel: 'Calidad de compresión',
      maxCompression: 'Máxima compresión',
      maxQuality: 'Máxima calidad',
      downloadExample: 'Descargar ejemplo →',
      compressMyPhotos: 'Comprimir mis fotos ↑',
    },
  },
  en: {
    uploader: {
      limitReachedTitle: 'Limit reached',
      limitReachedDesc: 'You already have many images. Remove some to add more.',
      fileLimitTitle: 'File limit',
      fileLimitDesc: (n) => `${n} file(s) could not be added.`,
      invalidFilesTitle: 'Invalid files',
      invalidFilesDesc: (n, formats) => `${n} file(s) are not valid ${formats} or exceed 50MB.`,
      noPendingTitle: 'No pending files',
      noPendingDesc: 'There are no pending images to compress.',
      zipFilePrefix: 'compressed_images',
      downloadCompleteTitle: 'Download complete',
      downloadCompleteDesc: (n, zipName) => `${n} image(s) downloaded in ${zipName}.`,
      noCompressedTitle: 'No compressed files',
      noCompressedDesc: 'There are no compressed images to download.',
      downloadErrorTitle: 'Download error',
      downloadErrorDesc: 'Could not create the ZIP file. Please try again.',
      dropzoneLimitTitle: 'Image limit reached',
      dropzoneLimitDesc: 'Remove some images to add more',
      dropHere: 'Drop the images here',
      willAppearHere: 'Your image will appear here and be optimized automatically',
      ctaButton: 'Upload your image — automatic optimization in seconds',
      reduceTime: 'Reduce the size in under 10 seconds',
      upToSize: (size) => `Up to ${size} per image`,
      processingAuto: '⏳ Processing image automatically...',
      compressingImages: 'Compressing images...',
      optimizedReady: (n) => n === 1 ? 'Optimized image ready' : `${n} optimized images ready`,
      doneSingle: 'Done! Your image is now optimized',
      doneMulti: (n) => `Done! Your ${n} images are now optimized`,
      readyToUse: 'Ready to use in forms, resumes, ID cards or email',
      sizeSummary: (a, b, pct) => `From ${a} to ${b} · ${pct}% less`,
      alreadyOptimized: 'The image was already optimized — best quality was preserved',
      creatingZip: 'Creating ZIP...',
      downloadAllZip: 'Download all (ZIP)',
      clear: 'Clear',
      tryAnother: 'Try another image',
    },
    imagePreview: {
      status: 'Status:',
      error: 'Error:',
    },
    actionButtons: {
      downloadOptimized: 'Download optimized image',
      readyToUse: 'Ready to use in forms, resumes, ID cards or email',
      retry: 'Retry',
      maxRetriesReached: 'Maximum retry attempts reached',
    },
    fileInfo: {
      original: 'Original: ',
      optimized: 'Optimized: ',
      saved: 'Saved: ',
      alreadyOptimized: 'Image already optimized — quality preserved',
    },
    statusBadge: {
      queued: 'Queued',
      processing: 'Processing...',
      completed: 'Completed',
      error: (retryCount, maxRetryAttempts) => `Error (${retryCount}/${maxRetryAttempts})`,
      unknown: 'Unknown',
    },
    imageComparison: {
      original: 'Original',
      compressed: 'Compressed',
      originalAlt: (fileName) => `Original ${fileName}`,
      compressedAlt: (fileName) => `Compressed ${fileName}`,
    },
    toasts: {
      dailyLimitTitle: 'Daily limit reached',
      dailyLimitDesc: (freeLimit) => `You have reached your limit of ${freeLimit} free compressions per day. Upgrade to Premium to compress unlimited images.`,
      dailyLimitDescShort: 'You have reached your limit of free compressions per day.',
      compressedTitle: 'Image compressed',
      compressedDesc: (fileName) => `${fileName} compressed successfully`,
      unknownCompressionError: 'Unknown error while compressing the image',
      compressionErrorTitle: 'Compression error',
      compressionErrorDesc: (fileName, errorMessage) => `Error in ${fileName}: ${errorMessage}`,
      compressionLimitTitle: 'Compression limit',
      compressionLimitDesc: (remaining) => `You can only compress ${remaining} more image(s) today. Upgrade to Premium to compress unlimited images.`,
      unlimited: 'Unlimited',
    },
    exampleImages: {
      heading: 'Try it with a sample image',
      landscape: 'Landscape',
      portrait: 'Portrait',
      product: 'Product',
      exampleAlt: (label) => `Example ${label}`,
      loading: 'Loading...',
    },
    compressionSlider: {
      originalAlt: 'Original image',
      uncompressed: (size) => `Uncompressed · ${size}`,
      compressedAlt: 'Compressed image',
      compressed: (size) => `Compressed · ${size}`,
      quality: 'Quality: ',
      savings: (percent, size) => `Savings: ${percent}% · ${size} less`,
      qualityAriaLabel: 'Compression quality',
      maxCompression: 'Max compression',
      maxQuality: 'Max quality',
      downloadExample: 'Download example →',
      compressMyPhotos: 'Compress my photos ↑',
    },
  },
};
